import { useCallback, useEffect, useState } from "react";

import type { TileJson } from "@deck.gl/geo-layers/dist/mvt-layer/mvt-layer";
import type { ResourceType } from "maplibre-gl";
import {
  isMapboxURL,
  transformMapboxUrl,
} from "maplibregl-mapbox-request-transformer";
import { useMap } from "react-map-gl/maplibre";

import {
  StaticsCacheStatus,
  selectOnline,
  selectStaticsCacheStatus,
  setStaticCacheStatus,
} from "@/features/app/appSlice";
import { BuildingState } from "@/features/cartography/cartographySlice";
import { selectConfigItem } from "@/features/config/configSlice";
import { selectLayerVisibility } from "@/features/map/mapSlice";

import { MAPBOX_API_KEY } from "@/constants";
import {
  appendBuildingsAction,
  getStyleFromCF,
  loadIMDf,
  setBuildingAction,
} from "@/features/cartography/actions";
import {
  selectCurrentBuildingID,
  selectCurrentBuildingState,
} from "@/features/cartography/cartographySlice.selectors";
import { isPMTilesRequest } from "@/features/map/libs/mapUtils";
import { LayerIndex } from "@/features/map/types";
import { useSitumAPIContext } from "@/features/situm_api/SitumAPIProvider";
import { BoundingBox, type Building, type PoiCategories } from "@/libs/models";
import {
  convertBoundingBoxToCorners,
  getTilesForBoundingBox,
} from "@/libs/tiles";
import { useAppDispatch, useAppSelector } from ".";
import useServiceWorker from "./useServiceWorker";

const usePrefetch = () => {
  const dispatch = useAppDispatch();
  const currentBuildingID = useAppSelector(selectCurrentBuildingID);
  const currentBuildingState = useAppSelector(selectCurrentBuildingState);
  const staticsCacheStatus = useAppSelector(selectStaticsCacheStatus);
  const isOnline = useAppSelector(selectOnline);
  const isTilePrefetchEnabled = useAppSelector(
    selectConfigItem("internal.prefetch.enableTilePrefetch"),
  );
  const minTileZoomToPrefetch = useAppSelector(
    selectConfigItem("internal.prefetch.minTileZoom"),
  );
  const maxTileZoomToPrefetch = useAppSelector(
    selectConfigItem("internal.prefetch.maxTileZoom"),
  );
  const isFloorPlanVisible = useAppSelector(
    selectLayerVisibility(LayerIndex.FLOORPLAN),
  );

  // State
  // This state is here so whenever we refresh data we do not run the prefetch
  const [hasPrefetchFinished, setHasPrefetchFinished] = useState(false);

  const {
    jobQueue,
    buildingsToPrefetch,
    poiCategoriesToPrefetch,
    getBuildingById,
    addPrefetchBuildingId,
    getJWT,
  } = useSitumAPIContext();
  const { map } = useMap();
  const { startPrecache: startSourcesPrecache } = useServiceWorker();

  /**
   * Wrapper over fetch with prefetch headers.
   */
  const prefetch = (url: string, headers?: HeadersInit) =>
    fetch(url, {
      ...(headers ? { headers: headers } : {}),
      // Only supported on firefox
      // headers: { 'Sec-Purpose': "prefetch" },
      priority: "low",
    });

  /**
   * Prefetch over buildings:
   *
   * - Fetches the list of buildings
   * - For each building it retrieves its information
   * - For each building's floor prefetches its floor plan
   *
   * Note: geojsons are fetched on the building mapper (should probably decouple this).
   */
  const prefetchBuildings = async (buildings: Building[]) => {
    Promise.allSettled(buildings.map((b) => prefetchBuilding(b.id))).then(
      (resolvedBuildings) => {
        const fulfilledBuildings = resolvedBuildings
          .filter((result) => result.status === "fulfilled")
          .map((result) => (result as PromiseFulfilledResult<Building>).value)
          // Avoid problems with invalid Buildings (i.e no center or corners)
          .filter((b) => b != null);
        appendBuildingsAction(fulfilledBuildings, BuildingState.PREFETCHED);
      },
    );
  };

  /**
   * Prefetches over POI categories:
   * - Fetches the list of POI categories
   * - Prefetches every icon of each POI category
   *
   * @param poiCategories List of POI categories to prefetch
   */
  const prefetchPoiCategories = (
    poiCategories: PoiCategories,
  ): Promise<any> => {
    return Promise.all(
      poiCategories
        ?.toFlatArray()
        .flatMap((category) =>
          [
            category.iconUrl &&
              jobQueue.enqueueUnique(category.iconUrl, () =>
                prefetch(category.iconUrl),
              ),
            category.selectedIconUrl &&
              jobQueue.enqueueUnique(category.selectedIconUrl, () =>
                prefetch(category.selectedIconUrl),
              ),
          ].filter(Boolean),
        ),
    );
  };

  /**
   * Prefetches the tiles for a given building
   * @param buildingId
   */
  const prefetchTiles = useCallback(
    async (building: Building) => {
      if (!building || !map?.isStyleLoaded || !isTilePrefetchEnabled) return;

      // Add authorization to our tiles
      const jwt = getJWT();
      const authHeaders = jwt ? { Authorization: `Bearer ${jwt}` } : undefined;

      const boundingBox = new BoundingBox(building.getBounds());
      const buildingCorners = convertBoundingBoxToCorners(boundingBox);
      const style = map.getStyle();
      const vectorSources = Object.values(style.sources).filter(
        (sourceData) => sourceData.type === "vector",
      );
      const tileJSONs = await Promise.all(
        vectorSources.map(async (source) => {
          try {
            const sourceURL = isMapboxURL(source.url)
              ? transformMapboxUrl(source.url, null, MAPBOX_API_KEY).url
              : source.url;
            const response = await fetch(sourceURL, {
              headers: isPMTilesRequest({
                resourceType: "Source" as ResourceType,
                url: sourceURL,
              })
                ? authHeaders
                : undefined,
            });
            if (!response.ok) {
              throw new Error(`Failed to fetch TileJSON from ${sourceURL}`);
            }
            const tileJSON = (await response.json()) as TileJson;
            return {
              maxzoom: tileJSON.maxzoom ?? 24,
              minzoom: tileJSON.minzoom ?? 0,
              tileSources: tileJSON.tiles,
            };
          } catch (err) {
            console.error(
              "Error loading TileJSON for source:",
              source.url,
              err,
            );
            return null;
          }
        }),
      );

      // Collect and wait for all prefetch promises
      const prefetchPromises = Array.from(
        { length: maxTileZoomToPrefetch - minTileZoomToPrefetch + 1 },
        (_, i) => minTileZoomToPrefetch + i,
      ).flatMap((z) => {
        const tiles = getTilesForBoundingBox({
          corners: buildingCorners,
          zoom: z,
        });
        return tiles.flatMap(({ z, x, y }) =>
          tileJSONs
            .filter((tileJSON) => tileJSON !== null)
            .flatMap(({ tileSources, maxzoom, minzoom }) =>
              z > maxzoom || z < minzoom
                ? []
                : tileSources.map((tileSource) => {
                    const tileSourceURL = tileSource
                      .replace("{z}", `${z}`)
                      .replace("{x}", `${x}`)
                      .replace("{y}", `${y}`);
                    return prefetch(tileSourceURL, authHeaders);
                  }),
            ),
        );
      });

      await Promise.all(prefetchPromises);
    },
    [
      map?.isStyleLoaded,
      minTileZoomToPrefetch,
      maxTileZoomToPrefetch,
      isTilePrefetchEnabled,
      getJWT,
    ],
  );

  /**
   * Prefetches information on a single building
   * @param buildingId
   */
  const prefetchBuilding = useCallback(
    async (buildingId: number) => {
      if (!buildingId) return;
      const building = await getBuildingById({ id: buildingId });
      if (!building) return;
      getStyleFromCF(building);
      // Make sure to load imdf on prefetch only if not loaded already
      const buildingWithIMDF = building.geojson
        ? building
        : await loadIMDf(building);
      setBuildingAction(buildingWithIMDF);

      // Wait until tiles have been prefetched
      if (currentBuildingID == buildingId) {
        await prefetchTiles(building);
      }

      // Prefetch floor images: do not wait for them to set building as prefetched
      isFloorPlanVisible &&
        building.floors?.map((f) =>
          jobQueue.enqueueUnique(f.floorplan.url, () =>
            prefetch(f.floorplan.url),
          ),
        );

      // Prefetch poi images: do not wait for them to set building as prefetched
      building.pois?.forEach((p) => {
        if (p.icon) {
          p.icon && jobQueue.enqueueUnique(p.icon, () => prefetch(p.icon));
          p.selectedIcon &&
            jobQueue.enqueueUnique(p.selectedIcon, () =>
              prefetch(p.selectedIcon),
            );
        }
      });

      building && addPrefetchBuildingId(building.id);
      return building;
    },
    [
      currentBuildingID,
      isFloorPlanVisible,
      getBuildingById,
      jobQueue,
      addPrefetchBuildingId,
    ],
  );

  // Prefetch stages:
  // 1. Source files precache
  // 2. Buildings prefetch
  // 3. Categories prefetch

  // Perform sources precache if static assets are not fully cached and current building has at least partially been loaded
  // This prefetch will re-execute whenever the user goes online. The sw checks which assets are already cached and only precaches the ones that are not.
  useEffect(() => {
    if (
      staticsCacheStatus === StaticsCacheStatus.checking ||
      staticsCacheStatus === StaticsCacheStatus.success ||
      currentBuildingState == null ||
      currentBuildingState === undefined
    ) {
      return;
    }
    dispatch(setStaticCacheStatus(StaticsCacheStatus.checking));
    startSourcesPrecache()
      .then(() => {
        dispatch(setStaticCacheStatus(StaticsCacheStatus.success));
      })
      .catch(() => {
        dispatch(setStaticCacheStatus(StaticsCacheStatus.failed));
      });
  }, [currentBuildingState, isOnline]);

  useEffect(() => {
    if (
      // Only perform prefetch once
      hasPrefetchFinished ||
      // Do not perform building prefetch until static assets have been downloaded
      staticsCacheStatus !== StaticsCacheStatus.success ||
      !buildingsToPrefetch ||
      buildingsToPrefetch.length === 0
    ) {
      return;
    }

    prefetchBuildings(buildingsToPrefetch);
  }, [buildingsToPrefetch, staticsCacheStatus]);

  useEffect(() => {
    if (
      // Only perform prefetch once
      hasPrefetchFinished ||
      !poiCategoriesToPrefetch ||
      poiCategoriesToPrefetch?.length() == 0
    ) {
      return;
    }

    // Do not perform poi categories prefetch until current building
    // finished prefetching
    if (currentBuildingState === BuildingState.PREFETCHED) {
      prefetchPoiCategories(poiCategoriesToPrefetch).then(() => {
        setHasPrefetchFinished(true);
      });
    }
  }, [poiCategoriesToPrefetch, currentBuildingState]);
};

export default usePrefetch;
